import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useChoicesApi } from '../../Hooks/useChoiceApi';
import { useSnackbar } from '../stateless/SnackBarProvider';

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
};

export const useAllChoices = (queryParams: Params) => {
  const { getAllChoices } = useChoicesApi();

  const serializedParams = JSON.stringify(queryParams);

  const {
    data,
    isLoading: isFetching,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['getChoices', serializedParams],
    // queryFn: () => getAllChoices(queryParams),
    queryFn: () => {
      // Always inject merchantId from localStorage if not present
      return getAllChoices({
        ...queryParams,
      });
    },
    enabled: Boolean(serializedParams),
    retry: false,
  });

  return {
    data,
    isLoading: isFetching || isRefetching,
    refetch,
  };
};


export const UseCreateChoice = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { createChoice } = useChoicesApi();
  return useMutation({
    mutationKey: ['CreateChoice'],
    mutationFn: ({ modifiedValues }: { modifiedValues: any; storeId: string }) => {
      return createChoice(modifiedValues);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Choice created successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getChoices'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to create Choice',
        'error'
      );
    },
  });
};

export const usePatchChoice = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { getToggleStatus } = useChoicesApi();

  return useMutation({
    mutationKey: ['PatchChoice'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      getToggleStatus({ id, status: data.status }),
    onSuccess: (_response, variables) => {
      const action =
        variables.data.status === 'unblocked' ? 'unblocked' : 'blocked';
      showMessage(`Choice ${action} successfully`, 'success');
      queryClient.invalidateQueries({
        queryKey: ['getChoices'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type || 'Failed to block the Choice',
        'error'
      );
    },
  });
};

export const useDeleteChoice = () => {
  const queryClient = useQueryClient();
  const { deleteChoice } = useChoicesApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteChoice'],
    mutationFn: (ids: string[]) => {
      return deleteChoice(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Choice deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getChoices'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to deleted Choice',
        'error'
      );
    },
  });
};

export const useEditChoice = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editChoice } = useChoicesApi();

  return useMutation({
    mutationKey: ['EditSubAdmin'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editChoice(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Choice Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getChoices'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the Choice',
        'error'
      );
    },
  });
};
